/**
 * Experiment Storage Utilities
 * 
 * Handles localStorage persistence and history management for chemistry experiments
 */

import { ChemExperimentState } from './types';

const STORAGE_KEY_PREFIX = 'chem_experiment_';
const HISTORY_KEY = 'chem_experiment_history';
const TRANSACTIONS_KEY = 'chem_experiment_transactions';
const CURRENT_EXPERIMENT_KEY = 'chem_current_experiment';

export type TransactionType = 
  | 'reaction_setup'
  | 'material_added'
  | 'material_removed'
  | 'calculation_run'
  | 'step_added'
  | 'step_removed'
  | 'manual_save';

export interface ExperimentTransaction {
  id: string;
  experimentId: string;
  transactionNumber: number; // Sequential number for this experiment
  type: TransactionType;
  timestamp: Date;
  description: string;
  experimentSnapshot: ChemExperimentState; // Full state at time of transaction
  details?: {
    // Additional context based on transaction type
    materialName?: string;
    calculationId?: string;
    stepName?: string;
    [key: string]: any;
  };
}

export interface ExperimentHistoryEntry {
  id: string;
  name: string;
  timestamp: Date;
  experiment: ChemExperimentState;
  // Legacy format - kept for backward compatibility
}

/**
 * Save current experiment to localStorage
 */
export function saveCurrentExperiment(experiment: ChemExperimentState): void {
  try {
    localStorage.setItem(CURRENT_EXPERIMENT_KEY, JSON.stringify(experiment));
  } catch (error) {
    console.error('Failed to save current experiment:', error);
  }
}

/**
 * Load current experiment from localStorage
 */
export function loadCurrentExperiment(): ChemExperimentState | null {
  try {
    const stored = localStorage.getItem(CURRENT_EXPERIMENT_KEY);
    if (!stored) return null;
    
    const experiment = JSON.parse(stored);
    // Convert date strings back to Date objects if needed
    if (experiment.assays) {
      experiment.assays = experiment.assays.map((assay: any) => ({
        ...assay,
      }));
    }
    return experiment;
  } catch (error) {
    console.error('Failed to load current experiment:', error);
    return null;
  }
}

/**
 * Save experiment to history
 */
export function saveToHistory(experiment: ChemExperimentState, name?: string): string {
  try {
    const history = getHistory();
    const entry: ExperimentHistoryEntry = {
      id: `exp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: name || experiment.reactionName || `Experiment ${experiment.experimentId}`,
      timestamp: new Date(),
      experiment: JSON.parse(JSON.stringify(experiment)), // Deep clone
    };
    
    history.unshift(entry); // Add to beginning
    
    // Keep only last 50 entries
    const trimmedHistory = history.slice(0, 50);
    
    localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmedHistory));
    return entry.id;
  } catch (error) {
    console.error('Failed to save to history:', error);
    return '';
  }
}

/**
 * Get all history entries
 */
export function getHistory(): ExperimentHistoryEntry[] {
  try {
    const stored = localStorage.getItem(HISTORY_KEY);
    if (!stored) return [];
    
    const history = JSON.parse(stored);
    // Convert timestamp strings back to Date objects
    return history.map((entry: any) => ({
      ...entry,
      timestamp: new Date(entry.timestamp),
    }));
  } catch (error) {
    console.error('Failed to load history:', error);
    return [];
  }
}

/**
 * Load experiment from history by ID
 */
export function loadFromHistory(id: string): ChemExperimentState | null {
  try {
    const history = getHistory();
    const entry = history.find((e) => e.id === id);
    return entry ? entry.experiment : null;
  } catch (error) {
    console.error('Failed to load from history:', error);
    return null;
  }
}

/**
 * Delete history entry by ID
 */
export function deleteHistoryEntry(id: string): void {
  try {
    const history = getHistory();
    const filtered = history.filter((e) => e.id !== id);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to delete history entry:', error);
  }
}

/**
 * Clear all history
 */
export function clearHistory(): void {
  try {
    localStorage.removeItem(HISTORY_KEY);
    localStorage.removeItem(TRANSACTIONS_KEY);
  } catch (error) {
    console.error('Failed to clear history:', error);
  }
}

/**
 * Save a transaction to history
 * Transactions are numbered sequentially per experiment
 */
export function saveTransaction(
  experiment: ChemExperimentState,
  type: TransactionType,
  description: string,
  details?: Record<string, any>
): string {
  try {
    const transactions = getTransactions();
    
    // Get next transaction number for this experiment
    const experimentTransactions = transactions.filter(
      (t) => t.experimentId === experiment.experimentId
    );
    const nextNumber = experimentTransactions.length > 0
      ? Math.max(...experimentTransactions.map((t) => t.transactionNumber)) + 1
      : 1;

    const transaction: ExperimentTransaction = {
      id: `txn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      experimentId: experiment.experimentId,
      transactionNumber: nextNumber,
      type,
      timestamp: new Date(),
      description,
      experimentSnapshot: JSON.parse(JSON.stringify(experiment)), // Deep clone
      details: details || {},
    };

    transactions.unshift(transaction); // Add to beginning

    // Keep only last 200 transactions
    const trimmedTransactions = transactions.slice(0, 200);

    localStorage.setItem(TRANSACTIONS_KEY, JSON.stringify(trimmedTransactions));
    return transaction.id;
  } catch (error) {
    console.error('Failed to save transaction:', error);
    return '';
  }
}

/**
 * Get all transactions, optionally filtered by experiment ID
 */
export function getTransactions(experimentId?: string): ExperimentTransaction[] {
  try {
    const stored = localStorage.getItem(TRANSACTIONS_KEY);
    if (!stored) return [];

    const transactions = JSON.parse(stored);
    // Convert timestamp strings back to Date objects
    const parsed = transactions.map((txn: any) => ({
      ...txn,
      timestamp: new Date(txn.timestamp),
    }));

    if (experimentId) {
      return parsed.filter((t: ExperimentTransaction) => t.experimentId === experimentId);
    }

    return parsed;
  } catch (error) {
    console.error('Failed to load transactions:', error);
    return [];
  }
}

/**
 * Get transactions grouped by experiment ID
 */
export function getTransactionsByExperiment(): Record<string, ExperimentTransaction[]> {
  const transactions = getTransactions();
  const grouped: Record<string, ExperimentTransaction[]> = {};

  transactions.forEach((txn) => {
    if (!grouped[txn.experimentId]) {
      grouped[txn.experimentId] = [];
    }
    grouped[txn.experimentId].push(txn);
  });

  // Sort transactions within each experiment by transaction number (descending)
  Object.keys(grouped).forEach((expId) => {
    grouped[expId].sort((a, b) => b.transactionNumber - a.transactionNumber);
  });

  return grouped;
}

/**
 * Load experiment state from a specific transaction
 */
export function loadFromTransaction(transactionId: string): ChemExperimentState | null {
  try {
    const transactions = getTransactions();
    const transaction = transactions.find((t) => t.id === transactionId);
    return transaction ? transaction.experimentSnapshot : null;
  } catch (error) {
    console.error('Failed to load from transaction:', error);
    return null;
  }
}

/**
 * Delete a transaction by ID
 */
export function deleteTransaction(transactionId: string): void {
  try {
    const transactions = getTransactions();
    const filtered = transactions.filter((t) => t.id !== transactionId);
    localStorage.setItem(TRANSACTIONS_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to delete transaction:', error);
  }
}

/**
 * Delete all transactions for an experiment
 */
export function deleteExperimentTransactions(experimentId: string): void {
  try {
    const transactions = getTransactions();
    const filtered = transactions.filter((t) => t.experimentId !== experimentId);
    localStorage.setItem(TRANSACTIONS_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to delete experiment transactions:', error);
  }
}

